import { useEffect, useState } from "react";
import { PlayCircle, Film, X } from "lucide-react";
import { API_BASE } from "@/lib/api";

const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// Plays a highlight — the player only loads after a tap (saves mobile data).
export const HighlightPlayer = ({ h, onClose }) => {
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
  <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-6" data-testid="highlight-player">
    <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-black border border-[#273042]">
      <div className="aspect-video">
        {h.youtubeId
          ? <iframe title={h.title} src={`https://www.youtube-nocookie.com/embed/${h.youtubeId}?autoplay=1&rel=0`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation" className="w-full h-full" />
          : h.vimeoEmbed
          ? <iframe title={h.title} src={`${h.vimeoEmbed}${h.vimeoEmbed.includes("?") ? "&" : "?"}autoplay=1&dnt=1`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation" className="w-full h-full" />
          : h.embedUrl
          ? <iframe title={h.title} src={h.embedUrl} allow="autoplay; fullscreen; encrypted-media" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation" className="w-full h-full bg-black" data-testid="highlight-embed" />
          : <video src={abs(h.url)} controls controlsList="nodownload" autoPlay playsInline preload="metadata" className="w-full h-full bg-black" />}
      </div>
      <div className="p-4 flex items-start gap-3"><div className="flex-1 min-w-0"><div className="text-white font-semibold">{h.title}</div>
        {(h.homeName || h.league) && <div className="text-xs text-slate-400 mt-0.5">{[h.homeName && h.awayName ? `${h.homeName} v ${h.awayName}` : "", h.league, h.matchDate].filter(Boolean).join(" · ")}</div>}</div>
        <button onClick={onClose} data-testid="highlight-close" aria-label="Close" className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
      </div>
    </div>
  </div>
  );
};

export const HighlightCard = ({ h, onPlay, wide = false }) => (
  <button onClick={() => onPlay(h)} data-testid={`highlight-card-${h.id}`}
    className={`group text-left rounded-xl overflow-hidden bg-[#121620] border border-[#273042] hover:border-[#E11D2A]/60 transition-colors ${wide ? "w-full" : "w-60 sm:w-64 shrink-0 snap-start"}`}>
    <div className="relative aspect-video bg-[#0A0C10]">
      {h.thumbnail ? <img src={abs(h.thumbnail)} alt="" loading="lazy" className="w-full h-full object-cover" /> : <Film className="w-8 h-8 text-slate-600 absolute inset-0 m-auto" />}
      <PlayCircle className="absolute inset-0 m-auto w-11 h-11 text-white/90 drop-shadow transition-transform group-hover:scale-110" />
    </div>
    <div className="p-3"><div className="text-sm text-white font-semibold line-clamp-2">{h.title}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{[h.league, h.matchDate].filter(Boolean).join(" · ")}</div></div>
  </button>
);

export function HighlightsStrip({ items }) {
  const [play, setPlay] = useState(null);
  if (!items?.length) return null;
  return (
    <section className="mt-8" data-testid="highlights-strip">
      <h2 className="text-base md:text-lg font-bold text-white mb-3 flex items-center gap-2"><Film className="w-5 h-5 text-[#E11D2A]" /> Highlights</h2>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x">{items.map((h) => <HighlightCard key={h.id} h={h} onPlay={setPlay} />)}</div>
      {play && <HighlightPlayer h={play} onClose={() => setPlay(null)} />}
    </section>
  );
}
